import { FC } from 'react';
import { IIcon, IIconColor, IconWrapper } from './IconWrapper';

// Creates a type for each record in the icons object. The keys a the union of the keys in the
// passed in object, and the type is FC with the prop types set by the second type parameter.
type IconMap<IconsObject, IconProps> = {
  [IconName in keyof IconsObject]: FC<IconProps>;
};

// A wrapper for the Object.entries function that outputs a typed array
// instead of [key: string, value: any]
const typedEntries = <IconName extends string | number | symbol, Component>(
  myObj: Record<IconName, Component>
): [IconName, Component][] => Object.entries(myObj) as [IconName, Component][];

// Reads the icons object
// Converts it to an array of key-value pairs, adding the types
// Loops over the array and creates a new object where the value (icon) is wrapped in the IconWrapper
export const wrapIcons = <IconsObject,>(
  icons: IconMap<IconsObject, IIconColor>
) =>
  typedEntries(icons).reduce<IconMap<IconsObject, IIcon>>(
    (obj, [iconName, Icon]) => {
      obj[iconName] = (props) => (
        <IconWrapper {...props}>
          <Icon {...props.color} />
        </IconWrapper>
      );
      return obj;
    },
    {} as IconMap<IconsObject, IIcon>
  );

type IColor = {
  defaultColor: string;
  color?: string;
};

export function getColor({ defaultColor, color }: IColor) {
  const usedColor = color || defaultColor;
  if (usedColor.startsWith('--')) return `var(${usedColor})`;
  return usedColor;
}

export const Icons = wrapIcons({
  Caret: ({ primary }: IIconColor) => (
    <svg
      fill={getColor({ defaultColor: 'none', color: primary })}
      role="img"
      aria-label="caret icon"
      viewBox="0 0 20 20"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M15.833 7.5 10 13.333 4.167 7.5"
      />
    </svg>
  ),
  CircleInfo: ({ primary }: IIconColor) => (
    <svg viewBox="0 0 18 17">
      <title>Circle info</title>
      <path
        fill={getColor({ defaultColor: '#212529', color: primary })}
        fillRule="evenodd"
        d="M17.447 8.66a8.25 8.25 0 1 1-16.5 0 8.25 8.25 0 0 1 16.5 0ZM9.939 5.61a.698.698 0 1 1-1.397 0 .698.698 0 0 1 1.397 0Zm-.037 6.114V7.72a.602.602 0 1 0-1.204 0v4.005a.602.602 0 0 0 1.204 0Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  CircleInfoBlack: ({ primary }: IIconColor) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 17">
      <path
        fill={getColor({ defaultColor: '#212529', color: primary })}
        fillRule="evenodd"
        d="M17.447 8.66a8.25 8.25 0 1 1-16.5 0 8.25 8.25 0 0 1 16.5 0ZM9.939 5.61a.698.698 0 1 1-1.397 0 .698.698 0 0 1 1.397 0Zm-.037 6.114V7.72a.602.602 0 1 0-1.204 0v4.005a.602.602 0 0 0 1.204 0Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  CircleInfoOutline: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11 14H10V10H9M10 6H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
        stroke={getColor({ defaultColor: '--info-900', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  CirclePromptBlack: ({ primary }: IIconColor) => (
    <svg viewBox="0 0 17 17">
      <path
        fill={getColor({ defaultColor: '#212529', color: primary })}
        fillRule="evenodd"
        d="M8.447 16.66a8.25 8.25 0 1 0 0-16.5 8.25 8.25 0 0 0 0 16.5Zm.337-6.74H7.767v-.504c0-.721.366-1.213 1.162-1.66.675-.389.936-.733.936-1.312v-.006c0-.683-.564-1.175-1.424-1.175-.878 0-1.419.487-1.494 1.213l-.006.055h-.994l.006-.07c.093-1.116.895-2.05 2.529-2.05 1.459 0 2.465.847 2.465 1.978v.005c0 .853-.471 1.46-1.215 1.88-.739.41-.948.716-.948 1.29v.355Zm.157 1.868c0 .35-.285.623-.663.623-.372 0-.663-.273-.663-.623 0-.355.291-.623.663-.623.378 0 .663.268.663.623Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  CircleWarningWhite: ({ primary }: IIconColor) => (
    <svg width="21" height="21" viewBox="0 0 21 21">
      <title>Invalid</title>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.86816 10.6381C2.86816 6.08178 6.56181 2.38812 11.1182 2.38812C15.6745 2.38812 19.3682 6.08177 19.3682 10.6381C19.3682 15.1945 15.6745 18.8881 11.1182 18.8881C6.56182 18.8881 2.86816 15.1945 2.86816 10.6381ZM10.3763 13.6898C10.3763 13.3041 10.689 12.9914 11.0747 12.9914C11.4604 12.9914 11.7731 13.3041 11.7731 13.6898C11.7731 14.0755 11.4604 14.3881 11.0747 14.3881C10.689 14.3881 10.3763 14.0755 10.3763 13.6898ZM10.4134 7.57485L10.4134 11.5796C10.4134 11.9119 10.6827 12.1812 11.015 12.1812C11.3472 12.1812 11.6166 11.9119 11.6166 11.5796L11.6166 7.57485C11.6166 7.24259 11.3472 6.97325 11.015 6.97325C10.6827 6.97325 10.4134 7.24259 10.4134 7.57485Z"
        fill={getColor({ defaultColor: 'white', color: primary })}
      />
    </svg>
  ),
  CircleCheckMarkValid: ({ primary, secondary }: IIconColor) => (
    <svg
      width="30"
      height="31"
      viewBox="0 0 30 31"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Valid</title>
      <g clipPath="url(#clip0_994_35535)">
        <circle
          cx="14.9993"
          cy="15.3802"
          r="14.9993"
          fill={getColor({ defaultColor: '#00B695', color: primary })}
        />
        <path
          d="M23.3261 9.12891L12.771 21.6284L6.66016 16.5149"
          stroke={getColor({ defaultColor: '#E5F8F4', color: secondary })}
          strokeWidth="2.99987"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_994_35535">
          <rect
            width="29.9987"
            height="29.9987"
            fill="white"
            transform="translate(0 0.380859)"
          />
        </clipPath>
      </defs>
    </svg>
  ),
  CheckMarkGreen: ({ primary }: IIconColor) => (
    <svg width="19" height="15" viewBox="0 0 19 15" fill="none">
      <path
        d="M17.11 1L6.907 13.0825L1 8.13966"
        stroke={getColor({ defaultColor: '#4B9B59', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  CheckMarkWhite: ({ primary }: IIconColor) => (
    <svg width="19" height="15" viewBox="0 0 19 15" fill="none">
      <path
        d="M17.11 1L6.907 13.0825L1 8.13966"
        stroke={getColor({ defaultColor: '--light-100', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  CloseButton: ({ primary, secondary }: IIconColor) => (
    <svg width="20" height="20" viewBox="0 0 20 20">
      <title>Close</title>
      <g clipPath="url(#a)">
        <path
          fill={getColor({ defaultColor: '#767676', color: primary })}
          d="M10 20a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z"
        />
        <path
          stroke={getColor({ defaultColor: '#fff', color: secondary })}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
          d="m14 6-8 8m0-8 8 8"
        />
      </g>
      <defs>
        <clipPath id="a">
          <path fill="#fff" d="M0 0h20v20H0z" />
        </clipPath>
      </defs>
    </svg>
  ),
  ArrowRight: ({ primary }: IIconColor) => (
    <svg
      width="17"
      height="15"
      viewBox="0 0 17 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="arrow right"
    >
      <path
        d="M0.5 7.86182C0.5 7.30953 0.947715 6.86182 1.5 6.86182L13.0858 6.86182L8.79289 2.56892C8.40237 2.1784 8.40237 1.54523 8.79289 1.15471C9.18342 0.764185 9.81658 0.764185 10.2071 1.15471L16.2071 7.15471C16.5976 7.54523 16.5976 8.1784 16.2071 8.56892L10.2071 14.5689C9.81658 14.9594 9.18342 14.9594 8.79289 14.5689C8.40237 14.1784 8.40237 13.5452 8.79289 13.1547L13.0858 8.86182L1.5 8.86182C0.947715 8.86182 0.5 8.4141 0.5 7.86182Z"
        fill={getColor({ defaultColor: '#212529', color: primary })}
      />
    </svg>
  ),
  TrustPilotStar: () => (
    <svg role="img" aria-label="trustpilot star" viewBox="0 0 46 46">
      <path d="M0 46.330002h46.375586V0H0z" fill="#00b67a"></path>
      <path
        fill="white"
        d="M39.533936 19.711433L13.230239 38.80065l3.838216-11.797827L7.02115 19.711433h12.418975l3.837417-11.798624 3.837418 11.798624h12.418975zM23.2785 31.510075l7.183595-1.509576 2.862114 8.800152L23.2785 31.510075z"
      ></path>
    </svg>
  ),
  AxoFinans: () => (
    <svg width="20.2" height="18.5" viewBox="0 0 113 103">
      <path
        d="M56.5 102.8c-29 0-48.9-7-50.5-7.5-2.1-.7-5.6-2.5-5.6-6.8V14.9c0-2.2 1-5.2 5.6-6.8C7.7 7.4 27 .3 56.5.3s48.8 7.1 50.6 7.8c4.6 1.6 5.6 4.5 5.6 6.8v73.6c0 3.2-1.9 5.5-5.5 6.8-1.7.6-20 7.5-50.7 7.5z"
        fill="#fff"
      />
      <path
        d="M109.5 89.3c0 1.5-1.1 2.2-2.8 2.8 0 0-18.4 7.5-50.2 7.5-29.9 0-50.2-7.5-50.2-7.5-1.6-.5-2.8-1.3-2.8-2.8V14.1c0-1.5 1.2-2.3 2.8-2.8 0 0 19.7-7.7 50.2-7.7 30.5 0 50.2 7.7 50.2 7.7 1.6.5 2.8 1.3 2.8 2.8v75.2z"
        fill="#ffc20e"
      />
      <path
        d="M17.5 71.2h9v2.6h-5.4v2.7h5.3v2.6h-5.3V84h-3.5V71.2zm12.7 0h3.5v12.9h-3.5V71.2zm7.7 0h4.4l3.2 6.3 1.1 2.8h.2l-.2-2.7v-6.3h3.3v12.9h-4.5l-3.3-6.6c-.5-1-.9-2-.9-2.5h-.1l.1 2v7.1h-3.3v-13zm28.6 12.9h-3.7l-.8-2.7h-4.8l-.9 2.6h-3.5l4.7-12.9H62l4.5 13zm-5.3-5.2-1-3-.5-2h-.1c-.1.7-.4 1.5-.6 2l-1 3h3.2zm8.3-7.7h4.4l3.2 6.3 1.1 2.8h.2l-.2-2.7v-6.3h3.3v12.9H77l-3.3-6.6c-.5-1-.9-2-.9-2.5h-.1l.1 2v7.1h-3.3v-13zm18.6 8.7a2 2 0 0 0 2.1 1.8c1 0 1.8-.4 1.8-1.2 0-.7-.4-1-1.7-1.4l-2.1-.7c-1.6-.6-3-1.7-3-3.5 0-2.7 2.4-4 5.4-4 3.7 0 4.3 2.2 4.6 2.9l-3.1 1c-.1-.4-.5-1.4-1.7-1.4s-1.7.5-1.7 1.1c0 .7.4.9 1.7 1.4l2.4.9c1.7.6 2.7 1.6 2.7 3.3 0 1.9-1.3 4.2-5.5 4.2-3.9 0-4.7-2.4-5-3.2l3.1-1.2z"
        fill="#fff"
      />
      <path
        d="M16.7 34.7c1.6-4.8 5.3-9 12.4-9 9.3 0 11.6 6.4 11.6 12.5v15.7l.2 8.9h-3.6l-.2-5.8c-2.9 3.2-6.3 6.6-11.9 6.6-6.4 0-10.2-4.4-10.2-10.2C15 42.3 25.2 41 34.6 41h2.5c-.2-6.5-.4-12-8.4-12-5.6 0-7.4 4-8.7 6.9l-3.3-1.2zm20.4 9.2h-2.4c-9.1 0-15.8 1.2-15.8 9 0 5.4 3.7 7.1 6.9 7.1 4.5 0 6.9-2.5 11.3-6.7v-9.4zm6.3-17.5h4.4l9 14.5 9.7-14.5h4.1L59.2 43.5l12.3 19.3H67L56.8 46.4 46.2 62.8h-4.4l12.6-19.1-11-17.3z"
        fill="#111"
      />
      <path
        d="M83.8 25.7c12.1 0 14.7 11.7 14.7 19.1 0 9.2-4.2 18.8-14.7 18.8S69.1 54 69.1 44.8c0-7.4 2.6-19.1 14.7-19.1zm0 34.6c6.7 0 10.9-5.5 10.9-16.3 0-7.4-3-15-10.9-15s-10.9 7.6-10.9 15c0 10.8 4.2 16.3 10.9 16.3zM96.3 28c0-1.5 1.2-2.3 2.4-2.3s2.4.8 2.4 2.3c0 1.5-1.2 2.3-2.4 2.3-1.3 0-2.4-.9-2.4-2.3zm4 0c0-1-.7-1.7-1.6-1.7-.9 0-1.7.7-1.7 1.7s.7 1.7 1.6 1.7c.9 0 1.7-.7 1.7-1.7zm-2 1.2h-.6v-2.5h.9c.7 0 1.1.2 1.1.8 0 .5-.3.6-.7.7l.6 1.1H99l-.6-1h-.2v.9zm.5-1.5c.2 0 .3-.1.3-.3 0-.2-.3-.3-.5-.3h-.3v.6h.5z"
        fill="#111"
      />
    </svg>
  ),
  AxoLaina: () => (
    <svg width="20.2" height="18.5" viewBox="0 0 113 103">
      <path
        d="M56.5 102.8c-29 0-48.9-7-50.5-7.5-2.1-.7-5.6-2.5-5.6-6.8V14.9c0-2.2 1-5.2 5.6-6.8C7.7 7.4 27 .3 56.5.3s48.8 7.1 50.6 7.8c4.6 1.6 5.6 4.5 5.6 6.8v73.6c0 3.2-1.9 5.5-5.5 6.8-1.7.6-20 7.5-50.7 7.5z"
        fill="#fff"
      />
      <path
        d="M109.5 89.3c0 1.5-1.1 2.2-2.8 2.8 0 0-18.4 7.5-50.2 7.5-29.9 0-50.2-7.5-50.2-7.5-1.6-.5-2.8-1.3-2.8-2.8V14.1c0-1.5 1.2-2.3 2.8-2.8 0 0 19.7-7.7 50.2-7.7 30.5 0 50.2 7.7 50.2 7.7 1.6.5 2.8 1.3 2.8 2.8v75.2z"
        fill="#ffc20e"
      />
      <path
        d="M16.7 34.7c1.6-4.8 5.3-9 12.4-9 9.3 0 11.6 6.4 11.6 12.5v15.7l.2 8.9h-3.6l-.2-5.8c-2.9 3.2-6.3 6.6-11.9 6.6-6.4 0-10.2-4.4-10.2-10.2C15 42.3 25.2 41 34.6 41h2.5c-.2-6.5-.4-12-8.4-12-5.6 0-7.4 4-8.7 6.9l-3.3-1.2zm20.4 9.2h-2.4c-9.1 0-15.8 1.2-15.8 9 0 5.4 3.7 7.1 6.9 7.1 4.5 0 6.9-2.5 11.3-6.7v-9.4zM43.4 26.4h4.4l9 14.5 9.7-14.5h4.1L59.2 43.5l12.3 19.3H67L56.8 46.4 46.2 62.8h-4.4l12.6-19.1-11-17.3z"
        fill="#111"
      />
      <path
        d="M83.8 25.7c12.1 0 14.7 11.7 14.7 19.1 0 9.2-4.2 18.8-14.7 18.8S69.1 54 69.1 44.8c0-7.4 2.6-19.1 14.7-19.1zm0 34.6c6.7 0 10.9-5.5 10.9-16.3 0-7.4-3-15-10.9-15s-10.9 7.6-10.9 15c0 10.8 4.2 16.3 10.9 16.3z"
        fill="#111"
      />
      <path
        d="M96.3 28c0-1.5 1.2-2.3 2.4-2.3s2.4.8 2.4 2.3-1.2 2.3-2.4 2.3c-1.3 0-2.4-.9-2.4-2.3zm4 0c0-1-.7-1.7-1.6-1.7S97 27 97 28s.7 1.7 1.6 1.7 1.7-.7 1.7-1.7zm-2 1.2h-.6v-2.5h.9c.7 0 1.1.2 1.1.8 0 .5-.3.6-.7.7l.6 1.1H99l-.6-1h-.2l.1.9zm.5-1.5c.2 0 .3-.1.3-.3 0-.2-.3-.3-.5-.3h-.3v.6h.5z"
        fill="#111"
      />
      <path
        d="M20.9 71.2v10.3h4.8v2.7h-8.4v-13h3.6zM40.5 71.2l4.6 13h-3.8l-.8-2.7h-4.8l-.9 2.7h-3.6l4.8-13h4.5zm-2.4 2.6-.6 2.1-1 3.1h3.2l-1.1-3.3-.5-1.9zM55.1 71.2v13h-3.6v-13h3.6zM67.2 71.2l2.9 5.7 1.5 3.5h.2l-.2-2.6v-6.6h3.3v13h-4.5l-3.2-6.4c-.7-1.3-1-2.4-1-2.7H66l.1 1.6v7.5h-3.3v-13h4.4zM90.6 71.2l4.6 13h-3.8l-.8-2.7h-4.8l-.9 2.7h-3.6l4.8-13h4.5zm-2.4 2.6-.6 2.1-1 3.1h3.2l-1.1-3.3-.5-1.9z"
        fill="#fff"
      />
    </svg>
  ),
  BankID: () => (
    <svg
      width="18"
      height="13"
      viewBox="0 0 18 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.3195 10.4205H13.028C12.8955 10.4205 12.7874 10.5254 12.7874 10.6549V11.7773C12.7874 11.9064 12.8955 12.0114 13.028 12.0114H17.3195C17.452 12.0114 17.5601 11.9064 17.5601 11.7773V10.6549C17.5601 10.5254 17.452 10.4205 17.3195 10.4205ZM4.59216 10.4205H0.300619C0.168156 10.4205 0.0600586 10.5254 0.0600586 10.6549V11.7773C0.0600586 11.9064 0.168156 12.0114 0.300619 12.0114H4.59216C4.72463 12.0114 4.83273 11.9064 4.83273 11.7773V10.6549C4.83273 10.5254 4.72463 10.4205 4.59216 10.4205ZM10.9559 7.23864H6.6644C6.53193 7.23864 6.42384 7.34357 6.42384 7.47313V8.59549C6.42384 8.72461 6.53193 8.82955 6.6644 8.82955H10.9559C11.0884 8.82955 11.1965 8.72461 11.1965 8.59549V7.47313C11.1965 7.34357 11.0884 7.23864 10.9559 7.23864ZM4.59216 7.23864H0.300619C0.168156 7.23864 0.0600586 7.34357 0.0600586 7.47313V8.59549C0.0600586 8.72461 0.168156 8.82955 0.300619 8.82955H4.59216C4.72463 8.82955 4.83273 8.72461 4.83273 8.59549V7.47313C4.83273 7.34357 4.72463 7.23864 4.59216 7.23864ZM17.3195 4.0555H13.0275C12.895 4.0555 12.7874 4.16044 12.7874 4.28999V5.41235C12.7874 5.54147 12.895 5.64641 13.028 5.64641H17.3195C17.452 5.64641 17.5601 5.54147 17.5601 5.41235V4.28999C17.5601 4.16044 17.452 4.0555 17.3195 4.0555ZM10.9559 4.05682H6.6644C6.53193 4.05682 6.42384 4.16176 6.42384 4.29131V5.41367C6.42384 5.54279 6.53193 5.64773 6.6644 5.64773H10.9559C11.0884 5.64773 11.1965 5.54279 11.1965 5.41367V4.29131C11.1965 4.16176 11.0884 4.05682 10.9559 4.05682ZM17.3195 0.875H13.028C12.8955 0.875 12.7874 0.979938 12.7874 1.10949V2.23185C12.7874 2.36097 12.8955 2.46591 13.028 2.46591H17.3195C17.452 2.46591 17.5601 2.36097 17.5601 2.23185V1.10949C17.5601 0.979938 17.452 0.875 17.3195 0.875ZM4.59216 0.875H0.300619C0.168156 0.875 0.0600586 0.979938 0.0600586 1.10949V2.23185C0.0600586 2.36097 0.168156 2.46591 0.300619 2.46591H4.59216C4.72463 2.46591 4.83273 2.36097 4.83273 2.23185V1.10949C4.83273 0.979938 4.72463 0.875 4.59216 0.875Z"
        fill="#212529"
      />
    </svg>
  ),
  ThumbUp: ({ primary }: IIconColor) => (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.09998 8.89998C2.09998 8.23723 2.63723 7.69998 3.29998 7.69998C3.96272 7.69998 4.49998 8.23723 4.49998 8.89998V13.7C4.49998 14.3627 3.96272 14.9 3.29998 14.9C2.63723 14.9 2.09998 14.3627 2.09998 13.7V8.89998Z"
        fill={getColor({ defaultColor: '#fff', color: primary })}
      />
      <path
        d="M5.29998 8.76664V13.1111C5.29998 13.7172 5.64238 14.2712 6.18443 14.5422L6.22431 14.5621C6.66865 14.7843 7.15861 14.9 7.65539 14.9H11.9883C12.751 14.9 13.4076 14.3616 13.5572 13.6138L14.5172 8.81376C14.7152 7.8237 13.958 6.89998 12.9483 6.89998H10.1V3.69998C10.1 2.81632 9.38363 2.09998 8.49998 2.09998C8.05815 2.09998 7.69998 2.45815 7.69998 2.89998V3.43331C7.69998 4.12569 7.47541 4.7994 7.05998 5.35331L5.93998 6.84664C5.52454 7.40055 5.29998 8.07426 5.29998 8.76664Z"
        fill={getColor({ defaultColor: '#fff', color: primary })}
      />
    </svg>
  ),
  LightBulb: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11 3C11 2.44772 10.5523 2 10 2C9.44771 2 9 2.44772 9 3V4C9 4.55228 9.44771 5 10 5C10.5523 5 11 4.55228 11 4V3Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
      <path
        d="M15.6568 5.75731C16.0473 5.36678 16.0473 4.73362 15.6568 4.34309C15.2663 3.95257 14.6331 3.95257 14.2426 4.34309L13.5355 5.0502C13.145 5.44072 13.145 6.07389 13.5355 6.46441C13.926 6.85494 14.5592 6.85494 14.9497 6.46441L15.6568 5.75731Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
      <path
        d="M18 10C18 10.5523 17.5523 11 17 11H16C15.4477 11 15 10.5523 15 10C15 9.44771 15.4477 9 16 9H17C17.5523 9 18 9.44771 18 10Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
      <path
        d="M5.05019 6.46443C5.44071 6.85496 6.07388 6.85496 6.4644 6.46443C6.85493 6.07391 6.85493 5.44074 6.4644 5.05022L5.7573 4.34311C5.36677 3.95259 4.73361 3.95259 4.34308 4.34311C3.95256 4.73363 3.95256 5.3668 4.34308 5.75732L5.05019 6.46443Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
      <path
        d="M5 10C5 10.5523 4.55228 11 4 11H3C2.44772 11 2 10.5523 2 10C2 9.44771 2.44772 9 3 9H4C4.55228 9 5 9.44771 5 10Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
      <path
        d="M8 16V15H12V16C12 17.1046 11.1046 18 10 18C8.89543 18 8 17.1046 8 16Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
      <path
        d="M12.0009 14C12.0155 13.6597 12.2076 13.3537 12.4768 13.1411C13.4046 12.4086 14 11.2738 14 10C14 7.79086 12.2091 6 10 6C7.79086 6 6 7.79086 6 10C6 11.2738 6.59545 12.4086 7.52319 13.1411C7.79241 13.3537 7.98451 13.6597 7.99911 14H12.0009Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
    </svg>
  ),
  Shield: ({ primary }: IIconColor) => (
    <svg
      width="52"
      height="54"
      viewBox="0 0 52 54"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.93169 10.6804C10.5581 10.5498 19.329 6.87698 26.0001 0.906189C32.6712 6.87698 41.4421 10.5498 51.0686 10.6804C51.4181 12.7629 51.6001 14.9022 51.6001 17.084C51.6001 33.8036 40.9146 48.0274 26.0001 53.2989C11.0857 48.0274 0.400146 33.8036 0.400146 17.084C0.400146 14.9022 0.58211 12.7629 0.93169 10.6804ZM36.5281 21.212C37.0407 20.6994 37.0407 19.8684 36.5281 19.3558C36.0156 18.8433 35.1846 18.8433 34.672 19.3558L22.8001 31.2278L17.3282 25.7558C16.8156 25.2433 15.9846 25.2433 15.472 25.7558C14.9594 26.2684 14.9594 27.0994 15.472 27.612L21.872 34.012C22.1181 34.2581 22.452 34.3964 22.8001 34.3964C23.1482 34.3964 23.482 34.2581 23.7282 34.012L36.5281 21.212Z"
        fill={getColor({ defaultColor: '--info-900', color: primary })}
      />
    </svg>
  ),
  Lifesaver: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18 10.684C18 15.1022 14.4183 18.684 10 18.684V20.684C15.5228 20.684 20 16.2068 20 10.684H18ZM10 18.684C5.58172 18.684 2 15.1022 2 10.684H0C0 16.2068 4.47715 20.684 10 20.684V18.684ZM2 10.684C2 6.26568 5.58172 2.68396 10 2.68396V0.68396C4.47715 0.68396 0 5.16111 0 10.684H2ZM10 2.68396C14.4183 2.68396 18 6.26568 18 10.684H20C20 5.16111 15.5228 0.68396 10 0.68396V2.68396ZM13 10.684C13 12.3408 11.6569 13.684 10 13.684V15.684C12.7614 15.684 15 13.4454 15 10.684H13ZM10 13.684C8.34315 13.684 7 12.3408 7 10.684H5C5 13.4454 7.23858 15.684 10 15.684V13.684ZM7 10.684C7 9.02711 8.34315 7.68396 10 7.68396V5.68396C7.23858 5.68396 5 7.92254 5 10.684H7ZM10 7.68396C11.6569 7.68396 13 9.02711 13 10.684H15C15 7.92254 12.7614 5.68396 10 5.68396V7.68396ZM15.6569 3.61289L12.1213 7.14843L13.5355 8.56264L17.0711 5.02711L15.6569 3.61289ZM12.1213 14.2195L15.6569 17.755L17.0711 16.3408L13.5355 12.8053L12.1213 14.2195ZM7.87868 7.14843L4.34315 3.61289L2.92893 5.02711L6.46447 8.56264L7.87868 7.14843ZM6.46447 12.8053L2.92893 16.3408L4.34315 17.755L7.87868 14.2195L6.46447 12.8053Z"
        fill={getColor({ defaultColor: '--info-900', color: primary })}
      />
    </svg>
  ),
  NoCost: ({ primary }: IIconColor) => (
    <svg
      width="16"
      height="21"
      viewBox="0 0 16 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5 12.684L11 6.68396M5.50003 7.18396H5.51003M10.5 12.184H10.51M15 19.684V3.68396C15 2.57939 14.1046 1.68396 13 1.68396H3C1.89543 1.68396 1 2.57939 1 3.68396V19.684L4.5 17.684L8 19.684L11.5 17.684L15 19.684ZM6 7.18396C6 7.4601 5.77614 7.68396 5.5 7.68396C5.22386 7.68396 5 7.4601 5 7.18396C5 6.90782 5.22386 6.68396 5.5 6.68396C5.77614 6.68396 6 6.90782 6 7.18396ZM11 12.184C11 12.4601 10.7761 12.684 10.5 12.684C10.2239 12.684 10 12.4601 10 12.184C10 11.9078 10.2239 11.684 10.5 11.684C10.7761 11.684 11 11.9078 11 12.184Z"
        stroke={getColor({ defaultColor: '--info-900', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Money: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="17"
      viewBox="0 0 20 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15 5.68396V3.68396C15 2.57939 14.1046 1.68396 13 1.68396H3C1.89543 1.68396 1 2.57939 1 3.68396V9.68396C1 10.7885 1.89543 11.684 3 11.684H5M7 15.684H17C18.1046 15.684 19 14.7885 19 13.684V7.68396C19 6.57939 18.1046 5.68396 17 5.68396H7C5.89543 5.68396 5 6.57939 5 7.68396V13.684C5 14.7885 5.89543 15.684 7 15.684ZM14 10.684C14 11.7885 13.1046 12.684 12 12.684C10.8954 12.684 10 11.7885 10 10.684C10 9.57939 10.8954 8.68396 12 8.68396C13.1046 8.68396 14 9.57939 14 10.684Z"
        stroke={getColor({ defaultColor: '--info-900', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Globe: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.05493 9.68396H3C4.10457 9.68396 5 10.5794 5 11.684V12.684C5 13.7885 5.89543 14.684 7 14.684C8.10457 14.684 9 15.5794 9 16.684V19.629M6 2.61948V4.18396C6 5.56467 7.11929 6.68396 8.5 6.68396H9C10.1046 6.68396 11 7.57939 11 8.68396C11 9.78853 11.8954 10.684 13 10.684C14.1046 10.684 15 9.78853 15 8.68396C15 7.57939 15.8954 6.68396 17 6.68396L18.0645 6.68396M13 19.1718V16.684C13 15.5794 13.8954 14.684 15 14.684H18.0645M19 10.684C19 15.6545 14.9706 19.684 10 19.684C5.02944 19.684 1 15.6545 1 10.684C1 5.7134 5.02944 1.68396 10 1.68396C14.9706 1.68396 19 5.7134 19 10.684Z"
        stroke={getColor({ defaultColor: '--info-900', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Reload: ({ primary }: IIconColor) => (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.65 3.03396C12.2 1.58396 10.21 0.68396 8.00001 0.68396C3.58001 0.68396 0.0100098 4.26396 0.0100098 8.68396C0.0100098 13.104 3.58001 16.684 8.00001 16.684C11.73 16.684 14.84 14.134 15.73 10.684H13.65C12.83 13.014 10.61 14.684 8.00001 14.684C4.69001 14.684 2.00001 11.994 2.00001 8.68396C2.00001 5.37396 4.69001 2.68396 8.00001 2.68396C9.66001 2.68396 11.14 3.37396 12.22 4.46396L9.00001 7.68396H16V0.68396L13.65 3.03396Z"
        fill={getColor({ defaultColor: '--info-900', color: primary })}
      />
    </svg>
  ),
  ShieldOutline: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7 11L9 13L13 9M18.6179 4.98433C18.4132 4.99471 18.2072 4.99996 18 4.99996C14.9265 4.99996 12.123 3.84452 9.99993 1.94432C7.87691 3.84445 5.07339 4.99984 2 4.99984C1.79277 4.99984 1.58678 4.99459 1.38213 4.98421C1.1327 5.94782 1 6.95841 1 8C1 13.5915 4.82432 18.2898 10 19.6219C15.1757 18.2898 19 13.5915 19 8C19 6.95846 18.8673 5.9479 18.6179 4.98433Z"
        stroke={getColor({ defaultColor: '--info-900', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Pdf: ({ primary }: IIconColor) => (
    <svg
      width="16"
      height="21"
      viewBox="0 0 16 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14 20.3679H2C0.89543 20.3679 0 19.4725 0 18.3679V2.36791C0 1.26334 0.89543 0.367906 2 0.367906H9C9.00896 0.366744 9.01804 0.366744 9.027 0.367906H9.033C9.04244 0.370864 9.05216 0.372874 9.062 0.373906C9.15019 0.37956 9.23726 0.396705 9.321 0.424906H9.336H9.351H9.363C9.38145 0.437832 9.39884 0.452211 9.415 0.467906C9.52394 0.51633 9.62321 0.584091 9.708 0.667906L15.708 6.66791C15.7918 6.75269 15.8596 6.85196 15.908 6.96091C15.917 6.98291 15.924 7.00391 15.931 7.02691L15.941 7.05491C15.9689 7.1383 15.9854 7.22509 15.99 7.31291C15.9909 7.32287 15.9932 7.33264 15.997 7.34191V7.34791C15.9986 7.35446 15.9996 7.36116 16 7.36791V18.3679C16 18.8983 15.7893 19.407 15.4142 19.7821C15.0391 20.1572 14.5304 20.3679 14 20.3679ZM10.424 12.3679V17.3679H11.364V15.3279H12.824V14.4899H11.364V13.2089H13V12.3679H10.424ZM6.724 12.3679V17.3679H7.93C8.43593 17.3928 8.92583 17.1868 9.262 16.8079C9.6069 16.3678 9.77967 15.8171 9.748 15.2589V14.4489C9.77117 13.8965 9.59356 13.3545 9.248 12.9229C8.92263 12.5525 8.44761 12.3486 7.955 12.3679H6.724ZM3 12.3679V17.3679H3.94V15.6089H4.566C4.98402 15.632 5.39232 15.4773 5.69 15.1829C5.97408 14.8653 6.12141 14.4485 6.1 14.0229C6.11856 13.5871 5.97135 13.1605 5.688 12.8289C5.40772 12.5181 5.00309 12.349 4.585 12.3679H3ZM9 2.36791V7.36791H14L9 2.36791ZM7.946 16.5299H7.664V13.2089H8.006C8.24885 13.1946 8.48236 13.3046 8.626 13.5009C8.77261 13.8042 8.83544 14.1412 8.808 14.4769V15.3459C8.82996 15.6657 8.7606 15.9851 8.608 16.2669C8.4441 16.4582 8.19651 16.5566 7.946 16.5299ZM4.585 14.7679H3.939V13.2089H4.594C4.75752 13.2107 4.90863 13.2964 4.994 13.4359C5.10222 13.6145 5.15447 13.8214 5.144 14.0299C5.15654 14.2244 5.1049 14.4176 4.997 14.5799C4.89754 14.7047 4.74447 14.7746 4.585 14.7679Z"
        fill={getColor({ defaultColor: '--dark-200', color: primary })}
      />
    </svg>
  ),
  Chevron: ({ primary }: IIconColor) => (
    <svg
      width="10"
      height="17"
      viewBox="0 0 10 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.5 1.5L8.5 8.5L1.5 15.5"
        stroke={getColor({ defaultColor: '--dark-900', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Emergency: ({ primary }: IIconColor) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16 0H2C0.9 0 0.00999999 0.9 0.00999999 2L0 16C0 17.1 0.9 18 2 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM16 16H2V2H16V16ZM7.5 14H10.5V10.5H14V7.5H10.5V4H7.5V7.5H4V10.5H7.5V14Z"
        fill={getColor({ defaultColor: '--info-900', color: primary })}
      />
    </svg>
  ),
  Telephone: ({ primary, secondary }: IIconColor) => (
    <svg
      width="21"
      height="23"
      viewBox="0 0 21 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Telephone</title>
      <g clipPath="url(#clip0_1203_40760)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M18.8332 15.5811V18.3311C18.8351 18.8473 18.6391 19.3404 18.2933 19.6894C17.9475 20.0384 17.4839 20.2109 17.0165 20.1644C14.4522 19.8579 11.989 18.894 9.82487 17.3502C7.81139 15.9428 6.10432 14.065 4.82487 11.8502C3.41652 9.45882 2.54007 6.73613 2.26654 3.90272C2.22443 3.39019 2.3801 2.88179 2.69551 2.50171C3.01092 2.12163 3.45703 1.90487 3.92487 1.90439H6.42487C7.26142 1.89533 7.97445 2.56986 8.09154 3.48105C8.19705 4.36111 8.39274 5.22522 8.67487 6.05689C8.90382 6.72686 8.75738 7.48213 8.29987 7.99105L7.24154 9.15522C8.42783 11.4501 10.1552 13.3503 12.2415 14.6552L13.2999 13.4911C13.7625 12.9878 14.4491 12.8267 15.0582 13.0786C15.8143 13.3889 16.5998 13.6041 17.3999 13.7202C18.2378 13.8503 18.8542 14.6504 18.8332 15.5811Z"
          fill={getColor({ defaultColor: '#212529', color: primary })}
          stroke={getColor({ defaultColor: '#F5F5F5', color: secondary })}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_1203_40760">
          <rect
            width="20"
            height="22"
            fill="white"
            transform="translate(0.5 0.0703125)"
          />
        </clipPath>
      </defs>
    </svg>
  ),
  Email: ({ primary, secondary }: IIconColor) => (
    <svg
      width="21"
      height="22"
      viewBox="0 0 21 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Email</title>
      <g clipPath="url(#clip0_1203_40764)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M3.83366 3.57031H17.167C18.0837 3.57031 18.8337 4.35781 18.8337 5.32031V15.8203C18.8337 16.7828 18.0837 17.5703 17.167 17.5703H3.83366C2.91699 17.5703 2.16699 16.7828 2.16699 15.8203V5.32031C2.16699 4.35781 2.91699 3.57031 3.83366 3.57031Z"
          fill={getColor({ defaultColor: '#212529', color: primary })}
          stroke={getColor({ defaultColor: '#F5F5F5', color: secondary })}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M18.8337 5.32031L10.5003 11.4453L2.16699 5.32031"
          fill={getColor({ defaultColor: '#212529', color: primary })}
        />
        <path
          d="M18.8337 5.32031L10.5003 11.4453L2.16699 5.32031"
          stroke={getColor({ defaultColor: '#F5F5F5', color: secondary })}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_1203_40764">
          <rect
            width="20"
            height="21"
            fill="white"
            transform="translate(0.5 0.0703125)"
          />
        </clipPath>
      </defs>
    </svg>
  ),
  Archive: ({ primary }: IIconColor) => (
    <svg
      width="14"
      height="12"
      viewBox="0 0 14 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.19998 0.399902C1.31632 0.399902 0.599976 1.11625 0.599976 1.9999C0.599976 2.88356 1.31632 3.5999 2.19998 3.5999H11.8C12.6836 3.5999 13.4 2.88356 13.4 1.9999C13.4 1.11625 12.6836 0.399902 11.8 0.399902H2.19998Z"
        fill={getColor({ defaultColor: '#fff', color: primary })}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.39998 4.3999H12.6V9.9999C12.6 10.8836 11.8836 11.5999 11 11.5999H2.99998C2.11632 11.5999 1.39998 10.8836 1.39998 9.9999V4.3999ZM5.39998 6.7999C5.39998 6.35808 5.75815 5.9999 6.19998 5.9999H7.79998C8.2418 5.9999 8.59998 6.35808 8.59998 6.7999C8.59998 7.24173 8.2418 7.5999 7.79998 7.5999H6.19998C5.75815 7.5999 5.39998 7.24173 5.39998 6.7999Z"
        fill={getColor({ defaultColor: '#fff', color: primary })}
      />
    </svg>
  ),
  CursorClick: ({ primary }: IIconColor) => (
    <svg
      width="21"
      height="20"
      viewBox="0 0 21 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.6668 14L12.6668 19L8.66682 8L19.6668 12L14.6668 14ZM14.6668 14L19.6668 19M6.85505 1.23853L7.63151 4.1363M4.80305 6.96472L1.90527 6.18826M13.6165 3.05029L11.4952 5.17161M5.83845 10.8284L3.71713 12.9497"
        stroke={getColor({ defaultColor: '#fff', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  House: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 12.5V15.5M10 12.5V15.5M14 12.5V15.5M1 19.5H19M1 8.5H19M1 5.5L10 1.5L19 5.5M2 8.5H18V19.5H2V8.5Z"
        stroke={getColor({ defaultColor: '#fff', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  CookieFilled: ({ primary }: IIconColor) => (
    <svg
      width="25"
      height="25"
      viewBox="0 0 25 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20.4274 9.32059V9.32392C20.8449 10.3292 21.0573 11.3975 21.0573 12.5002C21.0573 17.1031 17.3103 20.85 12.7075 20.85C8.1046 20.85 4.35767 17.1031 4.35767 12.5002C4.35767 7.8973 8.1046 4.15039 12.7075 4.15039C13.5772 4.15039 14.4366 4.2859 15.2548 4.5507C15.2582 4.5507 15.2582 4.55404 15.2582 4.55404H15.2687C15.2804 4.55803 15.2898 4.56516 15.2996 4.57249L15.3089 4.57928L15.3128 4.58202L15.3173 4.58486L15.3197 4.58626L15.3245 4.58888C15.3421 4.59938 15.3632 4.60272 15.3804 4.61655C15.3804 4.61655 15.3804 4.61769 15.381 4.61863C15.3814 4.61931 15.3823 4.61989 15.3837 4.61989C15.4024 4.63902 15.4159 4.65817 15.4313 4.67985L15.4357 4.68621C15.4378 4.68891 15.4399 4.69146 15.4421 4.69398L15.447 4.69937C15.4514 4.70417 15.4557 4.70897 15.4595 4.7143C15.4625 4.71838 15.4651 4.72278 15.4672 4.72773C15.4728 4.73751 15.4764 4.74817 15.4791 4.75931L15.4805 4.76553L15.4821 4.77313L15.4844 4.78688L15.4876 4.79568L15.4911 4.80476L15.4913 4.80544C15.4971 4.82047 15.503 4.83601 15.5054 4.85321C15.5054 4.85889 15.5047 4.86468 15.5039 4.87067V4.87343C15.503 4.88123 15.502 4.88949 15.502 4.89853C15.502 4.93001 15.4987 4.95769 15.4916 4.98919V4.99253C15.4875 5.00424 15.4804 5.01491 15.4736 5.02504C15.4683 5.03307 15.4631 5.04075 15.46 5.04835C15.4496 5.06935 15.4424 5.09034 15.4286 5.10751C14.9829 5.6505 14.7396 6.32898 14.7396 7.02463C14.7396 8.69076 16.096 10.0477 17.7627 10.0477C18.549 10.0477 19.2933 9.74524 19.8606 9.19558C19.8653 9.19169 19.87 9.18837 19.8747 9.18538C19.8814 9.1811 19.8882 9.1775 19.8949 9.17392C19.9009 9.17079 19.9068 9.16766 19.9126 9.16409C19.9337 9.15026 19.9475 9.13642 19.9685 9.12592H19.9756C20 9.11589 20.0281 9.11208 20.0558 9.10874C20.0729 9.10874 20.0906 9.09824 20.1078 9.10158C20.1288 9.10158 20.146 9.10874 20.1669 9.11208C20.1913 9.11542 20.2156 9.11875 20.2366 9.12592C20.2456 9.1303 20.2533 9.13634 20.2614 9.14293L20.2658 9.14659L20.2748 9.15359C20.3025 9.16743 20.3268 9.18127 20.3511 9.20227C20.3511 9.20427 20.3511 9.20507 20.3518 9.2054C20.3523 9.20561 20.3531 9.20561 20.3545 9.20561C20.3683 9.22278 20.3787 9.24376 20.3893 9.26476C20.3998 9.28194 20.417 9.29244 20.4241 9.31342C20.4241 9.31552 20.4241 9.31647 20.4247 9.31746L20.4257 9.31877L20.4274 9.32059ZM6.79294 13.5437C6.79294 14.3128 7.41611 14.9355 8.18474 14.9355C8.95337 14.9355 9.57654 14.3128 9.57654 13.5437C9.57654 12.7745 8.95337 12.1519 8.18474 12.1519C7.41562 12.1519 6.79294 12.775 6.79294 13.5437ZM10.2717 7.62917C9.6977 7.62917 9.22822 8.09914 9.22822 8.67312C9.22822 9.24712 9.69819 9.71709 10.2717 9.71662C10.8462 9.71662 11.3157 9.24712 11.3157 8.67312C11.3157 8.09864 10.8457 7.62917 10.2717 7.62917ZM12.3596 17.0229C12.3596 17.5974 12.8291 18.0669 13.4031 18.0669C13.9771 18.0669 14.4471 17.5969 14.4471 17.0229C14.4471 16.4489 13.9771 15.9794 13.4031 15.9794C12.8291 15.9794 12.3596 16.4489 12.3596 17.0229ZM12.7075 12.326C12.7075 13.0952 13.3306 13.7178 14.0993 13.7178C14.8679 13.7178 15.4906 13.0952 15.4911 12.326C15.4911 11.5569 14.8679 10.9343 14.0993 10.9343C13.3301 10.9343 12.7075 11.5574 12.7075 12.326Z"
        fill="currentColor"
      />
    </svg>
  ),
  Cookie: ({ primary }: IIconColor) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.14983 3.48184C8.59575 4.90156 9.97829 5.9 11.5 5.9C11.867 5.9 12.2079 5.83632 12.5218 5.73013C12.8368 8.11558 15.0096 9.91301 17.434 9.69981C17.5439 9.69858 17.648 9.69143 17.7467 9.68C18.0256 11.385 19.5153 12.6 21.2 12.6L21.2173 12.6C21.2384 12.6 21.2596 12.6 21.2807 12.6C20.9686 17.4368 16.9119 21.3 12 21.3C6.8866 21.3 2.7 17.1134 2.7 12C2.7 8.20948 4.93305 4.9749 8.14983 3.48184Z"
        stroke="white"
        strokeWidth="1.4"
      />
      <path
        d="M7.90002 10.1001C8.45231 10.1001 8.90002 9.65238 8.90002 9.1001C8.90002 8.54781 8.45231 8.1001 7.90002 8.1001C7.34774 8.1001 6.90002 8.54781 6.90002 9.1001C6.90002 9.65238 7.34774 10.1001 7.90002 10.1001Z"
        fill="white"
      />
      <path
        d="M8.30005 16.1001C8.85233 16.1001 9.30005 15.6524 9.30005 15.1001C9.30005 14.5478 8.85233 14.1001 8.30005 14.1001C7.74776 14.1001 7.30005 14.5478 7.30005 15.1001C7.30005 15.6524 7.74776 16.1001 8.30005 16.1001Z"
        fill="white"
      />
      <path
        d="M13.2 13C13.7522 13 14.2 12.5523 14.2 12C14.2 11.4477 13.7522 11 13.2 11C12.6477 11 12.2 11.4477 12.2 12C12.2 12.5523 12.6477 13 13.2 13Z"
        fill="white"
      />
      <path
        d="M14.7 17.7998C15.2522 17.7998 15.7 17.3521 15.7 16.7998C15.7 16.2475 15.2522 15.7998 14.7 15.7998C14.1477 15.7998 13.7 16.2475 13.7 16.7998C13.7 17.3521 14.1477 17.7998 14.7 17.7998Z"
        fill="white"
      />
    </svg>
  ),
  PieChart: ({ primary }: IIconColor) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21.2099 15.89C20.5737 17.3945 19.5787 18.7202 18.3118 19.7513C17.0449 20.7824 15.5447 21.4874 13.9424 21.8048C12.34 22.1221 10.6843 22.0421 9.12006 21.5718C7.55578 21.1014 6.13054 20.2551 4.96893 19.1067C3.80733 17.9582 2.94473 16.5428 2.45655 14.9839C1.96837 13.4251 1.86948 11.7705 2.16851 10.1646C2.46755 8.55878 3.15541 7.05063 4.17196 5.77203C5.18851 4.49343 6.5028 3.48332 7.99992 2.83M21.9999 12C21.9999 10.6868 21.7413 9.38642 21.2387 8.17317C20.7362 6.95991 19.9996 5.85752 19.071 4.92893C18.1424 4.00035 17.04 3.26375 15.8267 2.7612C14.6135 2.25866 13.3131 2 11.9999 2V12H21.9999Z"
        stroke="#101828"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  SpeechBubble: ({ primary }: IIconColor) => (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20.3 11.6001L20.3 11.6019C20.3031 12.8116 20.0206 14.0048 19.4752 15.0845L19.4738 15.0872C18.8263 16.3827 17.8311 17.4723 16.5992 18.234L16.5992 18.234C15.3675 18.9958 13.948 19.3995 12.4997 19.4001L12.4981 19.4001C11.2885 19.4033 10.0953 19.1207 9.01561 18.5753L8.7553 18.4438L8.47864 18.536L4.1068 19.9933L5.56408 15.6215L5.6563 15.3448L5.52482 15.0845C4.97946 14.0048 4.69684 12.8116 4.7 11.6019L4.7 11.6004C4.70056 10.1521 5.10432 8.73261 5.86607 7.50086C6.62782 6.2691 7.71745 5.27374 9.01294 4.62628L9.01294 4.62628L9.0156 4.62494C10.0953 4.07958 11.2885 3.79697 12.4982 3.80013H12.5H12.9803C14.8855 3.90987 16.6843 4.71616 18.0341 6.06596C19.3839 7.41576 20.1902 9.21452 20.3 11.1198L20.3 11.6001Z"
        stroke="white"
        strokeWidth="1.4"
      />
    </svg>
  ),
  LoadingGears: ({ primary }: IIconColor) => (
    <svg
      width="92"
      height="79"
      viewBox="0 0 92 79"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        opacity="0.5"
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.6313 32.0491C12.3359 32.7537 13.1817 33.5995 14.0273 34.586C14.8728 34.1632 15.8595 33.8814 16.846 33.7404C16.9869 32.4719 17.2687 31.3445 17.5506 30.4989C18.1143 30.358 18.8191 30.358 19.5238 30.358C20.2284 30.358 20.7923 30.358 21.4969 30.4989C21.7788 31.3445 21.9197 32.4721 22.2015 33.7404C23.188 33.8813 24.1747 34.1632 25.0203 34.586C25.8658 33.5995 26.5706 32.7537 27.2752 32.0491C28.5438 32.6128 29.6712 33.1765 30.7986 34.0222C30.5167 35.0087 30.094 35.9954 29.6712 37.2637C30.5168 37.8274 31.7853 39.3779 31.7853 39.3779C31.7853 39.3779 34.0403 38.5323 35.0268 38.2505C35.7314 39.3779 36.4363 40.5055 37 41.6329C36.2954 42.3375 35.4496 43.1832 34.4631 44.0288C34.7449 44.8744 35.0268 45.8611 35.1677 46.8475C36.4362 46.9884 37.5636 47.2703 38.5501 47.5521C38.5501 48.1158 38.691 48.8207 38.691 49.5253C38.691 50.2299 38.5501 50.7938 38.5501 51.4985C37.5636 51.7803 36.436 51.9212 35.1677 52.2031C35.0268 53.1895 34.7449 54.1762 34.4631 55.0218C35.4495 55.8674 36.2954 56.5722 37 57.2768C36.4363 58.5453 35.7314 59.6727 35.0268 60.8001C34.0404 60.5183 32.9127 60.0955 31.7853 59.6727C31.0807 60.5183 30.5168 61.0822 29.6712 61.7868C30.094 62.9142 30.5168 64.0418 30.7986 65.0283C29.6712 65.733 28.5436 66.4378 27.2752 67.0015C26.5706 66.2969 25.8657 65.4511 25.0203 64.4646C24.1747 64.7465 23.188 65.0283 22.2015 65.1692C21.9197 66.4378 21.7788 67.5652 21.4969 68.5516C20.7923 68.5516 20.2284 68.6925 19.5238 68.6925C18.8191 68.6925 18.1143 68.5516 17.5506 68.5516C17.2687 67.5652 16.9869 66.4375 16.846 65.1692C15.8595 65.0283 14.8728 64.7465 14.0273 64.4646C13.1817 65.4511 12.3359 66.2969 11.6313 67.0015C10.5039 66.4378 9.37632 65.733 8.24893 65.0283C8.53077 64.0419 8.81262 62.9142 9.37632 61.7868C8.53076 61.0822 7.82594 60.5183 7.26221 59.6727C5.99366 60.0955 5.00722 60.5183 4.0207 60.8001C3.17514 59.6727 2.61121 58.5451 2.04754 57.2768C2.75216 56.5722 3.59792 55.8673 4.58444 55.0218C4.16167 54.1762 3.87982 53.1895 3.73887 52.2031C2.47032 51.9212 1.34293 51.7803 0.497368 51.4985C0.356445 50.7938 0.356445 50.2299 0.356445 49.5253C0.356445 48.8207 0.356445 48.1158 0.497368 47.5521C1.34293 47.2703 2.47053 46.9884 3.73887 46.8475C3.8798 45.8611 4.16164 44.8744 4.58444 44.0288C3.59799 43.1832 2.75216 42.3375 2.04754 41.6329C2.61123 40.5055 3.17494 39.3779 4.0207 38.2505C5.00715 38.5323 5.99387 38.8142 7.26221 39.3779C7.8259 38.5323 8.53076 37.8275 9.37632 37.2637C8.81263 35.9952 8.53076 35.0088 8.24893 34.0222C9.37632 33.1767 10.5039 32.6127 11.6313 32.0491ZM19.5236 39.3779C25.161 39.3779 29.6707 43.888 29.6707 49.5251C29.6707 55.1622 25.1607 59.6722 19.5236 59.6722C13.8864 59.6722 9.37639 55.1622 9.37639 49.5251C9.37639 43.888 13.8864 39.3779 19.5236 39.3779Z"
        fill={getColor({ defaultColor: '#5A6570', color: primary })}
      >
        <animateTransform
          attributeType="xml"
          attributeName="transform"
          type="rotate"
          from="0 0 0"
          to="180 0 0"
          dur="2s"
          additive="sum"
          repeatCount="indefinite"
          calcMode="spline"
          keyTimes="0; 1"
          keySplines="0.42 0 0.58 1.0"
        />
      </path>
      <path
        opacity="0.8"
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M49.5497 31.735C50.584 32.3813 51.8255 33.157 53.1055 34.1007C53.9975 33.3637 55.0966 32.756 56.2342 32.3163C56.053 30.7647 56.0787 29.3426 56.182 28.2566C56.8154 27.9333 57.656 27.7393 58.4963 27.5453C59.3366 27.3513 60.0091 27.196 60.8882 27.1701C61.4571 28.1009 61.9356 29.4068 62.621 30.8418C63.8361 30.7382 65.0905 30.8027 66.2152 31.0741C66.952 29.6649 67.5597 28.4621 68.206 27.4279C69.874 27.7508 71.3737 28.1127 72.951 28.8109C72.8865 30.0649 72.654 31.358 72.499 32.987C73.6626 33.4264 74.5292 34.1117 75.6023 34.9261C76.7915 33.9435 78.0587 33.2969 79.1575 32.6892C80.3082 33.8396 81.4593 34.9903 82.4419 36.1796C81.7956 37.2139 81.0198 38.4554 80.0762 39.7354C80.6451 40.6661 81.2529 41.7653 81.6925 42.9028C83.2441 42.7216 84.6662 42.7474 85.9202 42.8119C86.0754 43.4841 86.4375 44.2859 86.6315 45.1262C86.8255 45.9665 86.8127 46.6778 87.0067 47.5181C85.9079 48.1258 84.6019 48.6043 83.167 49.2896C83.2706 50.5048 83.2061 51.7591 83.1028 52.8451C84.512 53.5819 85.7147 54.1896 86.749 54.8359C86.4261 56.5039 85.8959 58.0424 85.366 59.5809C84.112 59.5164 82.6508 59.3227 81.1899 59.1289C80.5824 60.3312 80.0652 61.159 79.2508 62.2321C80.0653 63.4602 80.88 64.6885 81.4877 65.7874C80.3372 66.9381 79.1866 68.0892 77.8292 69.1106C76.7949 68.4643 75.7214 67.6497 74.4415 66.706C73.5107 67.275 72.4116 67.8827 71.274 68.3224C71.2872 69.9128 71.4295 71.2961 71.365 72.5501C70.5247 72.7441 69.891 73.0674 69.0507 73.2614C68.2104 73.4554 67.331 73.4814 66.6588 73.6366C66.0511 72.5378 65.4045 71.2706 64.8872 69.7969C63.6721 69.9004 62.4177 69.836 61.3318 69.7327C60.595 71.1419 59.8192 72.3834 59.1729 73.4177C57.6733 73.0559 56.1345 72.5257 54.596 71.9959C54.6605 70.7419 54.6862 69.3195 55.048 67.8198C53.8456 67.2123 52.8498 66.7338 51.9448 65.8806C50.5483 66.7341 49.4884 67.5099 48.3895 68.1176C47.0707 67.0059 46.0877 65.8165 45.0663 64.4591C45.7126 63.4248 46.5272 62.3513 47.4708 61.0714C46.7339 60.1794 46.1261 59.0803 45.6864 57.9427C44.096 57.9559 42.7127 58.0982 41.6267 57.9949C41.2647 57.1934 41.1094 56.5209 40.9154 55.6806C40.7214 54.8403 40.5274 53.9997 40.5402 53.2887C41.471 52.7198 42.7381 52.0732 44.2119 51.5559C44.1084 50.3407 44.1728 49.0864 44.4442 47.9616C43.035 47.2248 41.7935 46.4491 40.7592 45.8028C41.121 44.3031 41.4828 42.8032 42.181 41.2259C43.435 41.2904 44.6893 41.3548 46.3571 41.6779C46.7965 40.5143 47.4431 39.4797 48.2962 38.5746C47.2747 37.217 46.667 36.1182 46.0593 35.0193C47.171 33.7006 48.3604 32.7176 49.5497 31.735V31.735ZM60.9795 38.3021C67.7024 36.75 74.3222 40.8869 75.8743 47.6094C77.4263 54.332 73.2895 60.9522 66.567 62.5042C59.8444 64.0563 53.2242 59.9195 51.6722 53.1969C50.1201 46.4744 54.2569 39.8542 60.9795 38.3021V38.3021Z"
        fill={getColor({ defaultColor: '#5A6570', color: primary })}
      >
        <animateTransform
          attributeType="xml"
          attributeName="transform"
          type="rotate"
          from="180 0 0"
          to="0 0 0"
          dur="2s"
          additive="sum"
          repeatCount="indefinite"
          calcMode="spline"
          keyTimes="0; 1"
          keySplines="0.42 0 0.58 1.0"
        />
      </path>
      <path
        opacity="0.2"
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M48.2614 2.34559C48.7574 2.8081 49.3527 3.36326 49.9513 4.01414C50.5163 3.70652 51.1808 3.49139 51.8483 3.37209C51.9141 2.50594 52.079 1.73246 52.2506 1.15066C52.6306 1.04142 53.11 1.02468 53.5893 1.00794C54.0685 0.991209 54.4521 0.977815 54.9347 1.05693C55.1464 1.62534 55.2691 2.38893 55.4909 3.24489C56.1652 3.31731 56.843 3.48557 57.4281 3.75305C57.9798 3.06204 58.4391 2.47001 58.9016 1.97403C59.7778 2.32729 60.558 2.68392 61.3448 3.23239C61.1766 3.91001 60.9125 4.59116 60.655 5.46386C61.2435 5.82717 61.6438 6.29318 62.1432 6.85155C62.8966 6.44138 63.6568 6.22289 64.3211 6.00777C64.8271 6.75783 65.3333 7.50802 65.7434 8.26142C65.2809 8.7574 64.7258 9.35273 64.0749 9.95127C64.2867 10.5197 64.5018 11.1841 64.6211 11.8517C65.4872 11.9174 66.2607 12.0823 66.9383 12.2506C66.9517 12.634 67.0643 13.11 67.081 13.5893C67.0978 14.0685 67.0153 14.4554 67.0321 14.9347C66.3678 15.1498 65.6042 15.2724 64.7483 15.4942C64.6759 16.1685 64.5076 16.8463 64.336 17.4281C65.027 17.9798 65.619 18.4391 66.115 18.9016C65.7617 19.7778 65.3091 20.5613 64.8567 21.3448C64.179 21.1766 63.402 20.9158 62.6252 20.655C62.166 21.2469 61.7959 21.6438 61.2375 22.1431C61.5518 22.8999 61.8662 23.6568 62.0813 24.3211C61.3312 24.8271 60.581 25.3333 59.7318 25.7468C59.2358 25.2843 58.7363 24.7258 58.1378 24.0749C57.5694 24.2867 56.9049 24.5018 56.2374 24.6211C56.0758 25.4906 56.0067 26.2607 55.8385 26.9383C55.3592 26.9551 54.979 27.0643 54.4998 27.081C54.0205 27.0978 53.5378 27.0187 53.1544 27.0321C52.9393 26.3678 52.7208 25.6075 52.5948 24.7483C51.9205 24.6759 51.2427 24.5076 50.6609 24.336C50.1093 25.027 49.5541 25.6224 49.0916 26.1184C48.3114 25.7617 47.5277 25.3091 46.7442 24.8567C46.9125 24.179 47.0774 23.4054 47.434 22.6252C46.8422 22.166 46.3494 21.7992 45.9459 21.2375C45.0931 21.5552 44.4323 21.8661 43.768 22.0813C43.1661 21.3346 42.7557 20.581 42.3423 19.7317C42.8048 19.2358 43.3633 18.7363 44.0142 18.1378C43.7065 17.5727 43.4914 16.9083 43.3721 16.2407C42.5026 16.0791 41.7325 16.0101 41.1507 15.8385C41.0381 15.3626 41.0247 14.979 41.008 14.4998C40.9912 14.0205 40.9745 13.5411 41.0569 13.1544C41.6253 12.9426 42.3856 12.7241 43.2449 12.5981C43.3173 11.9239 43.4856 11.2461 43.7531 10.6609C43.062 10.1092 42.4667 9.55409 41.9707 9.09159C42.3273 8.3114 42.6839 7.53108 43.2324 6.7442C43.91 6.91247 44.5878 7.08073 45.4639 7.43401C45.8272 6.84551 46.2898 6.34939 46.8516 5.94589C46.438 5.09648 46.2229 4.43225 46.0078 3.76797C46.7545 3.16608 47.508 2.75574 48.2614 2.34559V2.34559ZM53.8034 7.14283C57.6377 7.00893 60.812 9.96932 60.9459 13.8034C61.0798 17.6374 58.1194 20.812 54.2854 20.9459C50.4513 21.0798 47.2767 18.1194 47.1428 14.2854C47.0089 10.4513 49.9693 7.27672 53.8034 7.14283V7.14283Z"
        fill={getColor({ defaultColor: '#5A6570', color: primary })}
      >
        <animateTransform
          attributeType="xml"
          attributeName="transform"
          type="rotate"
          from="0 0 0"
          to="180 0 0"
          dur="2s"
          additive="sum"
          repeatCount="indefinite"
          calcMode="spline"
          keyTimes="0; 1"
          keySplines="0.42 0 0.58 1.0"
        />
      </path>
    </svg>
  ),
  BadgeCheck: ({ primary }: IIconColor) => (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.52032 4.64597C8.292 4.58439 9.02459 4.28094 9.61379 3.77883C10.9888 2.60709 13.011 2.60709 14.386 3.77883C14.9752 4.28094 15.7078 4.58439 16.4795 4.64597C18.2803 4.78968 19.7103 6.21965 19.854 8.02044C19.9155 8.79212 20.219 9.52471 20.7211 10.1139C21.8928 11.4889 21.8928 13.5112 20.7211 14.8861C20.219 15.4753 19.9155 16.2079 19.854 16.9796C19.7103 18.7804 18.2803 20.2104 16.4795 20.3541C15.7078 20.4157 14.9752 20.7191 14.386 21.2212C13.011 22.393 10.9888 22.393 9.61379 21.2212C9.02459 20.7191 8.292 20.4157 7.52032 20.3541C5.71953 20.2104 4.28955 18.7804 4.14585 16.9796C4.08427 16.2079 3.78082 15.4753 3.2787 14.8861C2.10697 13.5112 2.10697 11.4889 3.2787 10.1139C3.78082 9.52471 4.08427 8.79212 4.14585 8.02044C4.28955 6.21965 5.71953 4.78968 7.52032 4.64597ZM16.4484 10.9486C16.9171 10.4799 16.9171 9.72013 16.4484 9.2515C15.9798 8.78287 15.22 8.78287 14.7514 9.2515L10.7999 13.203L9.24843 11.6515C8.7798 11.1829 8.02 11.1829 7.55137 11.6515C7.08275 12.1201 7.08275 12.8799 7.55137 13.3486L9.95137 15.7486C10.42 16.2172 11.1798 16.2172 11.6484 15.7486L16.4484 10.9486Z"
        fill="#326196"
      />
    </svg>
  ),
  PuzzlePiece: ({ primary }: IIconColor) => (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.5001 3.67559C8.5001 3.01284 9.03736 2.47559 9.7001 2.47559C10.3628 2.47559 10.9001 3.01284 10.9001 3.67559V4.07559C10.9001 4.51741 11.2583 4.87559 11.7001 4.87559H14.1001C14.5419 4.87559 14.9001 5.23376 14.9001 5.67559V8.07559C14.9001 8.51741 14.5419 8.87559 14.1001 8.87559H13.7001C13.0374 8.87559 12.5001 9.41284 12.5001 10.0756C12.5001 10.7383 13.0374 11.2756 13.7001 11.2756H14.1001C14.5419 11.2756 14.9001 11.6338 14.9001 12.0756V14.4756C14.9001 14.9174 14.5419 15.2756 14.1001 15.2756H11.7001C11.2583 15.2756 10.9001 14.9174 10.9001 14.4756V14.0756C10.9001 13.4128 10.3628 12.8756 9.7001 12.8756C9.03736 12.8756 8.5001 13.4128 8.5001 14.0756V14.4756C8.5001 14.9174 8.14192 15.2756 7.7001 15.2756H5.3001C4.85827 15.2756 4.5001 14.9174 4.5001 14.4756V12.0756C4.5001 11.6338 4.14193 11.2756 3.7001 11.2756H3.3001C2.63736 11.2756 2.1001 10.7383 2.1001 10.0756C2.1001 9.41284 2.63736 8.87559 3.3001 8.87559H3.7001C4.14193 8.87559 4.5001 8.51741 4.5001 8.07559V5.67559C4.5001 5.23376 4.85827 4.87559 5.3001 4.87559H7.7001C8.14192 4.87559 8.5001 4.51741 8.5001 4.07559V3.67559Z"
        fill={getColor({ defaultColor: '#326196', color: primary })}
      />
    </svg>
  ),
  MoneyNotes: ({ primary }: IIconColor) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.39999 4.79999C3.29542 4.79999 2.39999 5.69542 2.39999 6.79999V12.4C2.39999 13.5046 3.29543 14.4 4.39999 14.4H4.79999V7.19999H16.8V6.79999C16.8 5.69542 15.9046 4.79999 14.8 4.79999H4.39999ZM7.19999 11.6C7.19999 10.4954 8.09542 9.59999 9.19999 9.59999H19.6C20.7046 9.59999 21.6 10.4954 21.6 11.6V17.2C21.6 18.3046 20.7046 19.2 19.6 19.2H9.19999C8.09542 19.2 7.19999 18.3046 7.19999 17.2V11.6ZM14.4 16.8C15.7255 16.8 16.8 15.7255 16.8 14.4C16.8 13.0745 15.7255 12 14.4 12C13.0745 12 12 13.0745 12 14.4C12 15.7255 13.0745 16.8 14.4 16.8Z"
        fill="currentColor"
      />
    </svg>
  ),
  CreditCard: ({ primary }: IIconColor) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.79996 4.79999C3.47448 4.79999 2.39996 5.8745 2.39996 7.19999V8.39999H21.6V7.19999C21.6 5.8745 20.5254 4.79999 19.2 4.79999H4.79996Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21.6 10.8H2.39996V16.8C2.39996 18.1255 3.47448 19.2 4.79996 19.2H19.2C20.5254 19.2 21.6 18.1255 21.6 16.8V10.8ZM4.79996 15.6C4.79996 14.9372 5.33722 14.4 5.99996 14.4H7.19996C7.8627 14.4 8.39996 14.9372 8.39996 15.6C8.39996 16.2627 7.8627 16.8 7.19996 16.8H5.99996C5.33722 16.8 4.79996 16.2627 4.79996 15.6ZM10.8 14.4C10.1372 14.4 9.59996 14.9372 9.59996 15.6C9.59996 16.2627 10.1372 16.8 10.8 16.8H12C12.6627 16.8 13.2 16.2627 13.2 15.6C13.2 14.9372 12.6627 14.4 12 14.4H10.8Z"
        fill="currentColor"
      />
    </svg>
  ),
  Burger: ({ primary }: IIconColor) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21 18H3"
        stroke={getColor({ defaultColor: '#FFFFFF', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21 12H3"
        stroke={getColor({ defaultColor: '#FFFFFF', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21 6H3"
        stroke={getColor({ defaultColor: '#FFFFFF', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Cross: ({ primary }: IIconColor) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.7279 18.7282L6 6.00024"
        stroke={getColor({ defaultColor: '#FFFFFF', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.7279 6.27183L6 18.9998"
        stroke={getColor({ defaultColor: '#FFFFFF', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Minus: ({ primary }: IIconColor) => (
    <svg width="19" height="2" viewBox="0 0 19 2" fill="none">
      <path
        d="M18.5 1C18.5 1.19891 18.421 1.38968 18.2803 1.53033C18.1397 1.67098 17.9489 1.75 17.75 1.75H1.25C1.05109 1.75 0.860322 1.67098 0.71967 1.53033C0.579018 1.38968 0.5 1.19891 0.5 1C0.5 0.801088 0.579018 0.610322 0.71967 0.46967C0.860322 0.329018 1.05109 0.25 1.25 0.25H17.75C17.9489 0.25 18.1397 0.329018 18.2803 0.46967C18.421 0.610322 18.5 0.801088 18.5 1Z"
        fill={getColor({ defaultColor: 'none', color: primary })}
      />
    </svg>
  ),
  Plus: ({ primary }: IIconColor) => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M18 9C18 9.19891 17.921 9.38968 17.7803 9.53033C17.6397 9.67098 17.4489 9.75 17.25 9.75H9.75V17.25C9.75 17.4489 9.67098 17.6397 9.53033 17.7803C9.38968 17.921 9.19891 18 9 18C8.80109 18 8.61032 17.921 8.46967 17.7803C8.32902 17.6397 8.25 17.4489 8.25 17.25V9.75H0.75C0.551088 9.75 0.360322 9.67098 0.21967 9.53033C0.0790178 9.38968 0 9.19891 0 9C0 8.80109 0.0790178 8.61032 0.21967 8.46967C0.360322 8.32902 0.551088 8.25 0.75 8.25H8.25V0.75C8.25 0.551088 8.32902 0.360322 8.46967 0.21967C8.61032 0.0790178 8.80109 0 9 0C9.19891 0 9.38968 0.0790178 9.53033 0.21967C9.67098 0.360322 9.75 0.551088 9.75 0.75V8.25H17.25C17.4489 8.25 17.6397 8.32902 17.7803 8.46967C17.921 8.61032 18 8.80109 18 9Z"
        fill={getColor({ defaultColor: 'none', color: primary })}
      />
    </svg>
  ),
});
